/**
 * Жизненный цикл раскрытия панели фильтров без DOM (DEN-65).
 *
 * Разделы панели нельзя держать смонтированными в закрытом состоянии: счётчики
 * в них обновляются каждый кадр. Поэтому закрытие не снимает их сразу, а ждёт
 * конца перехода высоты, и только потом разделы пропадают из дерева.
 */

/** Раскрытие, мс. Единственный источник: `filter-panel.tsx` берёт его в `transitionDuration`. */
export const OPEN_DURATION_MS = 180;
/** Закрытие, мс. Единственный источник: `filter-panel.tsx` берёт его в `transitionDuration`. */
export const CLOSE_DURATION_MS = 140;
/** Запас страховочного таймера сверх длительности закрытия: `transitionend` не гарантирован. */
export const CLOSE_TIMER_MARGIN_MS = 60;
/** Через сколько после закрытия сработает страховка, если `transitionend` не пришёл. */
export const CLOSE_FALLBACK_MS = CLOSE_DURATION_MS + CLOSE_TIMER_MARGIN_MS;

/**
 * Страховка фазы `opening`, мс: если кадр анимации не пришёл (вкладка неактивна,
 * `requestAnimationFrame` приостановлен), фаза всё равно завершится через
 * длительность раскрытия, а не зависнет с закрытой обёрткой.
 */
export const OPEN_FALLBACK_MS = OPEN_DURATION_MS;

/**
 * - `closed` — разделов нет в дереве;
 * - `opening` — смонтированы, обёртка ещё `closed` (ждём кадр, чтобы был переход);
 * - `open` — раскрыто;
 * - `closing` — обёртка `closed`, разделы ещё в дереве до конца перехода.
 */
export type PresencePhase = "closed" | "opening" | "open" | "closing";

export type PresenceEvent = "open" | "close" | "frame" | "transitionEnd" | "timeout";

/** Следующая фаза. Событие, не имеющее смысла в фазе, фазу не меняет. */
export function nextPhase(
  phase: PresencePhase,
  event: PresenceEvent,
  reducedMotion: boolean,
): PresencePhase {
  switch (event) {
    case "open":
      if (phase === "open" || phase === "opening") return phase;
      // Повторное открытие во время закрытия отменяет снятие: разделы уже в дереве.
      if (phase === "closing") return "open";
      return reducedMotion ? "open" : "opening";
    case "close":
      if (phase === "closed" || phase === "closing") return phase;
      return reducedMotion ? "closed" : "closing";
    case "frame":
      return phase === "opening" ? "open" : phase;
    case "transitionEnd":
    case "timeout":
      return phase === "closing" ? "closed" : phase;
  }
}

export interface PresenceView {
  /** Рисовать ли разделы. */
  mounted: boolean;
  /** Значение `data-state` обёртки. */
  dataState: "open" | "closed";
  /** Обёртка недоступна для фокуса и вспомогательных технологий. */
  inert: boolean;
}

export function presenceView(phase: PresencePhase): PresenceView {
  return {
    mounted: phase !== "closed",
    dataState: phase === "open" ? "open" : "closed",
    inert: phase === "closing",
  };
}
